import { motion } from 'framer-motion'
import {
  Clock,
  Shield,
  Users,
  Heart,
  Home,
  Activity,
  CheckCircle,
  Calendar
} from 'lucide-react'

import Section from '../components/ui/Section'
import GlassCard from '../components/ui/GlassCard'
import ServicePackages from '../components/services/ServicePackages'

const Services = () => {

  const mainServices = [
    {
      icon: Clock,
      title: 'ACTIVITÉS DE JOUR',
      description: 'Les Résidences Massena Inc. offrent à leur résidents une programmation journalière très variée et adaptée au besoin et la capacité de chaque usager. Ces activités visent à combattre l\'isolement chez nos résidents pour un vivre-ensemble continu et convivial.',
      features: [
        'Préposé(e) aux bénéficiaires sur place 24/7',
        'Distribution et gestion de la médication (selon la loi 90)',
        'Entretien ménager et buanderie',
        'Internet & Câblodistribution',
        'Air climatisé',
        'Accompagnement aux visites médicales',
        'Équipe de travail professionnel',
        'Convivialité 24/7 support aux usagers',
        'Professionnalisme auprès des agents publics des services sociaux',
        'Relations étroites avec les parents des usagers',
        'Partenariats avec les centres médicaux et para-médicaux',
        'Suivi quotidien avec les établissements publics de transports adaptés'
      ],
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Home,
      title: 'SERVICES D\'HÉBERGEMENT ADAPTÉ ET SÉCURITAIRE',
      description: 'Les Résidences Masséna Inc. dispose des maisons de répit et des ressources pouvant faciliter les familles à retrouver un "chez soi" pour les personnes adultes ayant des déficiences intellectuelles.',
      features: [
        'RÉPIT DE JOUR',
        'RÉPIT PAR ALTERNANCE',
        'RÉPIT DE LONGUE DURÉE',
        'Chambres individuelles et partagées',
        'Espaces communs adaptés',
        'Environnement sécurisé'
      ],
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Activity,
      title: 'ACTIVITÉS DE LA VIE QUOTIDIENNE (AVQ)',
      description: 'Avec le support d\'un personnel qualifié nos résidents développent quotidiennement des compétences pour gérer leur besoins physiques de base, y compris l\'hygiène du corps, l\'habillage, le transfert ou la marche et l\'alimentation.',
      features: [
        'Développement de l\'autonomie',
        'Support personnalisé',
        'Formation aux compétences de base',
        'Accompagnement quotidien'
      ],
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Users,
      title: 'INCLUSION SOCIALE',
      description: 'Nous luttons chaque jour pour l\'inclusion et l\'affirmation des personnes à déficience intellectuelle en milieu social spécifiquement sur le marché du travail.',
      features: [
        'Intégration professionnelle',
        'Développement des compétences sociales',
        'Partenariats avec les employeurs',
        'Accompagnement vers l\'autonomie'
      ],
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: Heart,
      title: 'LOISIR ET RELAXATION',
      description: 'Les Résidences Masséna Inc. est un environnement social qui favorise un soutien personnalisé à chaque résident. Ainsi des activités de divertissement sont réalisées en vue d\'améliorer le fonctionnement et le développement des résidents, notamment leur estime de soi, la fierté et le bien-être.',
      features: [
        'Activités de divertissement',
        'Développement de l\'estime de soi',
        'Programmes de bien-être',
        'Soutien personnalisé'
      ],
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: Shield,
      title: 'SÉCURITÉ ET SURVEILLANCE',
      description: 'Systèmes de sécurité avancés et surveillance 24/7 pour la tranquillité d\'esprit des familles.',
      features: [
        'Système de caméra de surveillance à l\'entrée principale',
        'Détecteur de fumée',
        'Système d\'alarme relié à une centrale',
        'Plan d\'évacuation conforme',
        'Surveillance 24/7'
      ],
      color: 'from-teal-500 to-cyan-500',
    },
  ]


  return (
    <div className="min-h-screen pt-32 pb-20">
      <Section
        title="Nos Services"
        subtitle="Des services adaptés pour le bien-être et l'autonomie des personnes en déficience intellectuelle"
      >
        {/* Main Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mainServices.map((service, index) => {
            const Icon = service.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <GlassCard className="h-full space-y-6 group">
                  <div className="relative">
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.color} blur-2xl opacity-20 group-hover:opacity-30 transition-opacity`} />
                    <div className="relative bg-primary-100 p-6 rounded-xl inline-flex">
                      <Icon className="w-10 h-10 text-primary-600" />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-black mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    
                    {/* Features List */}
                    {service.features && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-black mb-2">Services inclus :</h4>
                        <ul className="space-y-1">
                          {service.features.slice(0, 4).map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </li>
                          ))}
                          {service.features.length > 4 && (
                            <li className="text-xs text-gray-500 italic">
                              + {service.features.length - 4} autres services...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.3, duration: 0.8 }}
                    className={`h-1 bg-gradient-to-r ${service.color} rounded-full`}
                  />
                </GlassCard>
              </motion.div>
            )
          })}
        </div>

        {/* Service Packages */}
        <ServicePackages />

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <GlassCard className="text-center p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-primary-600/10" />
            <div className="relative z-10">
              <h3 className="text-3xl font-display font-bold gradient-text mb-4">
                Une Question Sur Nos Services ?
              </h3>
              <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                Notre équipe est à votre disposition pour répondre à toutes vos questions
              </p>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl font-semibold text-white shadow-lg"
              >
                Contactez-nous
              </motion.a>
            </div>
          </GlassCard>
        </motion.div>
      </Section>
    </div>
  )
}

export default Services

